/**
 * Notches v1 — audit page (matches WFS dashboard design + SpxAudit shape).
 *
 * Mirrors SpxAudit two-panel layout:
 *   - Left: list of runs (per-symbol filtered)
 *   - Right: pipeline visualization + event timeline for selected run
 *
 * Symbol tabs filter the run list by which symbol's audit events to show.
 * Auth-gated by notchesEnabled + at least one symbol subscription.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessSymbol } from '../../lib/entitlements';
import '../Dashboard.css';
import {
  notchesService,
  NotchesSymbol,
  NotchesAuditEvent,
  NotchesAuditRunSummary,
} from '../../services/notchesService';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  success: '#16a34a',
  failure: '#dc2626',
  warning: '#d97706',
  skip:    '#6b7280',
};

// Notches pipeline steps (from notches handler.py audit calls)
const STEP_LABELS: Record<string, string> = {
  init:        'Init',
  data_load:   'Data Load',
  filter:      'Universe Filter',
  analytics:   'Skew + Macro',
  scoring:     'Scoring',
  ai_tier:     'AI Tier',
  storage:     'Storage',
};

const STEP_ORDER = ['init', 'data_load', 'filter', 'analytics', 'scoring', 'ai_tier', 'storage'];

const ALL_SYMBOLS: NotchesSymbol[] = ['SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'GC', 'CL'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function fmtDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function getUserSymbols(user: any): NotchesSymbol[] {
  return (['SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'GC', 'CL'] as NotchesSymbol[]).filter((s) => canAccessSymbol(user, s));
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatusDot: React.FC<{ status: string; size?: number }> = ({ status, size = 10 }) => (
  <span style={{
    width: size, height: size, borderRadius: '50%',
    background: STATUS_COLORS[status] || '#6b7280',
    display: 'inline-block', flexShrink: 0,
  }} />
);

const RunCard: React.FC<{ run: NotchesAuditRunSummary; selected: boolean; onClick: () => void }> = ({ run, selected, onClick }) => {
  const statusColor = run.errors > 0 ? '#dc2626' : '#16a34a';
  return (
    <div
      onClick={onClick}
      style={{
        background: selected ? '#1e293b' : '#111827',
        border: selected ? '1px solid #3b82f6' : '1px solid #1f2937',
        borderRadius: 8, padding: '12px 14px', cursor: 'pointer', marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 600 }}>
          {run.startedAt ? fmtDate(run.startedAt) : 'Unknown'}
        </span>
        <StatusDot status={run.errors > 0 ? 'failure' : 'success'} />
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#9ca3af' }}>
        <span style={{ fontWeight: 600 }}>{run.symbol}</span>
        <span>{run.totalEvents} events</span>
        {run.totalDurationMs && <span>{fmtDuration(run.totalDurationMs)}</span>}
        {run.errors > 0 && <span style={{ color: statusColor }}>{run.errors} err</span>}
      </div>
    </div>
  );
};

const StepPipeline: React.FC<{ run: NotchesAuditRunSummary }> = ({ run }) => {
  const stepMap: Record<string, NotchesAuditRunSummary['steps'][0]> = {};
  for (const s of run.steps) stepMap[s.step] = s;

  return (
    <div style={{
      display: 'flex', gap: 0, alignItems: 'center', marginBottom: 16,
      overflowX: 'auto', padding: '8px 0',
    }}>
      {STEP_ORDER.map((step, i) => {
        const info = stepMap[step];
        const status = info?.status || 'skip';
        const color = STATUS_COLORS[status] || '#6b7280';
        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <div style={{
                width: 24, height: 2, background: info ? color : '#374151',
                flexShrink: 0,
              }} />
            )}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              minWidth: 80, flexShrink: 0,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: info ? `${color}20` : '#1f2937',
                border: `2px solid ${info ? color : '#374151'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 4,
              }}>
                {status === 'success' && <span style={{ color, fontSize: 14 }}>&#10003;</span>}
                {status === 'failure' && <span style={{ color, fontSize: 14 }}>&#10007;</span>}
                {status === 'warning' && <span style={{ color, fontSize: 14 }}>!</span>}
                {status === 'skip' && <span style={{ color: '#6b7280', fontSize: 12 }}>—</span>}
              </div>
              <span style={{ fontSize: 10, color: info ? '#e5e7eb' : '#6b7280', textAlign: 'center' }}>
                {STEP_LABELS[step] || step}
              </span>
              {info?.duration_ms != null && (
                <span style={{ fontSize: 9, color: '#6b7280', fontFamily: 'monospace' }}>
                  {fmtDuration(info.duration_ms)}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const TimelineEvent: React.FC<{ event: NotchesAuditEvent; expanded: boolean; onClick: () => void }> = ({ event, expanded, onClick }) => {
  const typeLabel = event.event_type.replace(/_/g, ' ');
  return (
    <div style={{ marginBottom: 2 }}>
      <div
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
          cursor: 'pointer', borderRadius: 6,
          background: expanded ? '#1e293b' : 'transparent',
        }}
      >
        <StatusDot status={event.status} />
        <span style={{ color: '#9ca3af', fontSize: 11, width: 60, flexShrink: 0, fontFamily: 'monospace' }}>
          {fmtTime(event.timestamp)}
        </span>
        <span style={{ color: '#e5e7eb', fontSize: 13, flex: 1, textTransform: 'capitalize' }}>
          {typeLabel}
        </span>
        {event.summary && (
          <span style={{ color: '#9ca3af', fontSize: 12, flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {event.summary}
          </span>
        )}
        {event.duration_ms != null && (
          <span style={{ color: '#6b7280', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            {fmtDuration(event.duration_ms)}
          </span>
        )}
      </div>
      {expanded && event.detail && (
        <div style={{
          margin: '4px 0 8px 20px', padding: '10px 14px',
          background: '#0f172a', borderRadius: 6, border: '1px solid #1f2937',
        }}>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
            <span>Step: {STEP_LABELS[event.step] || event.step}</span>
            <span style={{ marginLeft: 12 }}>Symbol: {event.symbol}</span>
          </div>
          <pre style={{
            color: '#d1d5db', fontSize: 12, margin: 0,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace',
          }}>
            {JSON.stringify(event.detail, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const NotchesAudit: React.FC = () => {
  const { user, token } = useAuth();
  const userSymbols = useMemo(() => getUserSymbols(user), [user]);
  const [symbol, setSymbol] = useState<NotchesSymbol>(() => userSymbols[0] ?? 'SPX');
  const [runs, setRuns] = useState<NotchesAuditRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runEvents, setRunEvents] = useState<NotchesAuditEvent[]>([]);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
  );

  useEffect(() => {
    if (userSymbols.length > 0 && !userSymbols.includes(symbol)) {
      setSymbol(userSymbols[0]);
    }
  }, [userSymbols, symbol]);

  const fetchRuns = useCallback(async () => {
    if (!token || !userSymbols.includes(symbol)) return;
    setLoading(true);
    setError(null);
    try {
      const data = await notchesService.getAuditRuns(token, symbol, selectedDate);
      setRuns(data.runs || []);
      if (data.runs?.length > 0) {
        setSelectedRunId(data.runs[0].runId);
      } else {
        setSelectedRunId(null);
        setRunEvents([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate, symbol, userSymbols]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  useEffect(() => {
    if (!token || !selectedRunId) return;
    setLoadingEvents(true);
    notchesService.getAuditRunEvents(token, selectedRunId)
      .then(data => setRunEvents(data.events || []))
      .catch(() => setRunEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [token, selectedRunId]);

  const selectedRun = runs.find(r => r.runId === selectedRunId);

  const totalRuns = runs.length;
  const successRuns = runs.filter(r => r.status === 'success').length;
  const errorRuns = runs.filter(r => r.errors > 0).length;
  const avgDuration = runs.reduce((sum, r) => sum + (r.totalDurationMs || 0), 0) / Math.max(totalRuns, 1);

  // Empty-subs gate
  if (userSymbols.length === 0) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: '#f9fafb' }}>
          <h2 style={{ marginBottom: '0.75rem' }}>Notches Audit</h2>
          <p style={{ color: '#9ca3af', maxWidth: 500, margin: '0 auto 1.5rem' }}>
            Notches audit is gated per symbol. Subscribe to SPX, SPY, ES, or MES to see audit events here.
          </p>
          <a href="/upgrade" style={{ display: 'inline-block', backgroundColor: '#6d28d9', color: 'white', padding: '0.75rem 1.5rem', textDecoration: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: 14 }}>
            View subscriptions
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px', color: '#e5e7eb' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>Notches Audit</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              style={{
                background: '#111827', color: '#e5e7eb', border: '1px solid #374151',
                borderRadius: 6, padding: '6px 10px', fontSize: 13,
              }}
            />
            <button
              onClick={fetchRuns}
              style={{
                background: '#1f2937', color: '#9ca3af', border: '1px solid #374151',
                borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13,
              }}
            >Refresh</button>
          </div>
        </div>

        {/* Symbol tabs (only user's subscribed symbols) */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {userSymbols.map((s) => {
            const active = symbol === s;
            return (
              <button
                key={s}
                onClick={() => setSymbol(s)}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600,
                  border: `1px solid ${active ? '#3b82f6' : '#374151'}`,
                  background: active ? '#3b82f6' : '#1f2937',
                  color: active ? '#fff' : '#9ca3af',
                  borderRadius: 6, cursor: 'pointer',
                }}
              >
                {s}
              </button>
            );
          })}
        </div>

        {/* Stats bar */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {[
            { label: 'Runs', value: totalRuns, color: '#3b82f6' },
            { label: 'Success', value: successRuns, color: '#16a34a' },
            { label: 'Errors', value: errorRuns, color: errorRuns > 0 ? '#dc2626' : '#6b7280' },
            { label: 'Avg Duration', value: fmtDuration(Math.round(avgDuration)) || '—', color: '#d97706' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#111827', borderRadius: 8, padding: '12px 20px',
              border: '1px solid #1f2937', flex: 1, textAlign: 'center',
            }}>
              <div style={{ color: s.color, fontSize: 24, fontWeight: 700 }}>{s.value}</div>
              <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Two-panel layout */}
        <div style={{ display: 'flex', gap: 16, minHeight: 500 }}>
          {/* Left: run list */}
          <div style={{ width: 300, flexShrink: 0 }}>
            {loading ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>Loading runs…</div>
            ) : runs.length === 0 ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>
                No {symbol} runs found for {selectedDate}
              </div>
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
                {runs.map(run => (
                  <RunCard
                    key={run.runId}
                    run={run}
                    selected={selectedRunId === run.runId}
                    onClick={() => { setSelectedRunId(run.runId); setExpandedEvent(null); }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: pipeline + timeline */}
          <div style={{
            flex: 1, background: '#111827', borderRadius: 8,
            border: '1px solid #1f2937', padding: '16px 20px',
            overflowY: 'auto', maxHeight: 'calc(100vh - 280px)',
          }}>
            {!selectedRunId ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 60 }}>
                Select a run to view its pipeline
              </div>
            ) : loadingEvents ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 60 }}>Loading events…</div>
            ) : (
              <>
                {selectedRun && <StepPipeline run={selectedRun} />}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 12px' }}>
                  <div style={{ height: 1, flex: 1, background: '#1f2937' }} />
                  <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                    Event Timeline
                  </span>
                  <div style={{ height: 1, flex: 1, background: '#1f2937' }} />
                </div>
                {runEvents.length === 0 ? (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>No events for this run</div>
                ) : (
                  <div style={{ borderLeft: '2px solid #1f2937', marginLeft: 14, paddingLeft: 6 }}>
                    {runEvents.map(event => (
                      <TimelineEvent
                        key={event.event_id}
                        event={event}
                        expanded={expandedEvent === event.event_id}
                        onClick={() => setExpandedEvent(expandedEvent === event.event_id ? null : event.event_id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
